import { useEffect, useRef } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';

/**
 * Hook to detect when deploy effects are used and play appropriate sound effects
 * - shrink.mp3 for units that shrink other units (e.g., Kendrex Toxengineer)
 * - grow.mp3 for units that grow other units
 * - confuse.mp3 for units that get confused
 * Stat/status markers with sourceType `strategem` are skipped — those use useStrategemSoundEffects
 * (e.g. Hive Surge +2 attack also calls markDeployEffect, which would otherwise double-play grow.mp3).
 */
export const useDeployEffectSounds = (gameState, userId) => {
  const previousEffectsRef = useRef(new Set());
  const isFirstRenderRef = useRef(true);

  // Mapping of unit IDs that shrink other units
  const shrinkUnitIds = new Set([
    166  // Kendrex Toxengineer
    // Add more unit IDs that shrink units here
  ]);

  // Mapping of unit IDs that grow other units
  const growUnitIds = new Set([
    68  // Asrah's Final Judgement - grows units for +5/+0
    // Add more unit IDs that grow units here
  ]);

  useEffect(() => {
    if (!gameState?.state || !userId) return;

    // Skip detection on first render
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      
      // Build initial set of effects
      const allPlayerIds = gameState.players || Object.keys(gameState.state);
      const initialEffects = new Set();
      
      allPlayerIds.forEach(playerId => {
        const player = gameState.state[playerId];
        if (!player?.playedOrders) return;
        
        ['front', 'flank'].forEach(zone => {
          if (player.playedOrders[zone]) {
            player.playedOrders[zone].forEach((unit) => {
              if (!unit || !unit.instanceId) return;
              
              const enhancedBy = Array.isArray(unit.enhancedBy) 
                ? unit.enhancedBy 
                : (unit.enhancedBy ? [unit.enhancedBy] : []);
              
              enhancedBy.forEach((enhancement) => {
                if (enhancement && enhancement.instanceId) {
                  // Include status in key for proper tracking of status effects
                  const statusKey = enhancement.effectType === 'status' 
                    ? (enhancement.status || (enhancement.statuses && enhancement.statuses.join('_')) || '')
                    : '';
                  const key = `${playerId}_${unit.instanceId}_${enhancement.instanceId}_${enhancement.effectType}_${statusKey}_${enhancement.turn || 'unknown'}`;
                  initialEffects.add(key);
                }
              });
            });
          }
        });
      });
      
      previousEffectsRef.current = initialEffects;
      return;
    }

    const allPlayerIds = gameState.players || Object.keys(gameState.state);
    const newEffects = [];

    // Detect newly applied deploy effects
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.playedOrders) return;
      
      ['front', 'flank'].forEach(zone => {
        if (player.playedOrders[zone]) {
          player.playedOrders[zone].forEach((unit) => {
            if (!unit || !unit.instanceId) return;
            
            const enhancedBy = Array.isArray(unit.enhancedBy) 
              ? unit.enhancedBy 
              : (unit.enhancedBy ? [unit.enhancedBy] : []);
            
            enhancedBy.forEach((enhancement) => {
              if (enhancement && enhancement.instanceId) {
                // Include status in key for proper tracking of status effects
                const statusKey = enhancement.effectType === 'status' 
                  ? (enhancement.status || (enhancement.statuses && enhancement.statuses.join('_')) || '')
                  : '';
                const key = `${playerId}_${unit.instanceId}_${enhancement.instanceId}_${enhancement.effectType}_${statusKey}_${enhancement.turn || 'unknown'}`;
                
                // Check if this is a new effect
                if (!previousEffectsRef.current.has(key)) {
                  if (enhancement.sourceType === 'strategem') {
                    return;
                  }
                  // Find the source unit that applied this effect
                  // Check if this is a shrink, grow, or confuse effect
                  const isShrinkEffect = enhancement.effectType === 'stat' && 
                                        (enhancement.healthChange < 0 || enhancement.attackChange < 0);
                  const isGrowEffect = enhancement.effectType === 'stat' && 
                                      (enhancement.healthChange > 0 || enhancement.attackChange > 0);
                  const isConfuseEffect = enhancement.effectType === 'status' && 
                                         (enhancement.status === 'confused' || 
                                          enhancement.statuses?.includes('confused') ||
                                          (enhancement.status && enhancement.status.includes('confused')));
                  
                  if (isShrinkEffect || isGrowEffect || isConfuseEffect) {
                    let sourceUnit = null;
                    
                    // Try to find source unit by instanceId
                    allPlayerIds.forEach(searchPlayerId => {
                      const searchPlayer = gameState.state[searchPlayerId];
                      if (!searchPlayer || sourceUnit) return;
                      
                      ['front', 'flank'].forEach(searchZone => {
                        if (searchPlayer.playedOrders?.[searchZone]) {
                          const found = searchPlayer.playedOrders[searchZone].find(
                            (su) => su && su.instanceId === enhancement.instanceId
                          );
                          if (found) {
                            sourceUnit = found;
                          }
                        }
                      });
                    });
                    
                    // If not found by instanceId, try to get unitId from enhancement
                    if (!sourceUnit && enhancement.unitId) {
                      // We have the unitId, but we'd need to search by ID which is less reliable
                      // For now, we'll use the enhancement data itself
                      sourceUnit = {
                        id: enhancement.unitId,
                        name: enhancement.name || 'Unknown Unit',
                        instanceId: enhancement.instanceId
                      };
                    }
                    
                    if (sourceUnit) {
                      newEffects.push({
                        sourceUnit,
                        targetUnit: unit,
                        effectType: enhancement.effectType,
                        healthChange: enhancement.healthChange,
                        attackChange: enhancement.attackChange,
                        status: enhancement.status,
                        statuses: enhancement.statuses,
                        key
                      });
                    }
                  }
                }
              }
            });
          });
        }
      });
    });

    // Play sounds for newly detected effects
    if (newEffects.length > 0) {
      newEffects.forEach(({ sourceUnit, targetUnit, healthChange, attackChange, status, statuses }, index) => {
        const delay = index * 100; // Stagger sounds if multiple effects happen simultaneously
        
        setTimeout(() => {
          const sourceUnitId = sourceUnit.id;
          let soundPath = null;

          // Check for confuse effect first (status effects)
          const isConfused = status === 'confused' || 
                           statuses?.includes('confused') ||
                           (status && status.includes('confused'));
          
          if (isConfused) {
            soundPath = '/sounds/confuse.mp3';
            console.log(`🔊 Playing confuse sound for deploy effect: ${targetUnit.name} got confused by ${sourceUnit.name} (ID: ${sourceUnitId})`);
          } else if (sourceUnitId === 166) {
            // Kendrex Toxengineer (166): neurotox chevrons + neurotox.mp3 via useNeurotoxPulse
            return;
          } else if (shrinkUnitIds.has(sourceUnitId) || healthChange < 0 || attackChange < 0) {
            soundPath = '/sounds/shrink.mp3';
            console.log(`🔊 Playing shrink sound for deploy effect: ${sourceUnit.name} (ID: ${sourceUnitId}) shrinking ${targetUnit.name}`);
          } else if (growUnitIds.has(sourceUnitId) || healthChange > 0 || attackChange > 0) {
            // Resistance Commander (280): hive_surge chevrons + sound via useHiveSurgePulse
            if (Number(sourceUnitId) === 280) {
              return;
            }
            soundPath = '/sounds/grow.mp3';
            console.log(`🔊 Playing grow sound for deploy effect: ${sourceUnit.name} (ID: ${sourceUnitId}) growing ${targetUnit.name}`);
          }

          if (soundPath) {
            try {
              playSoundEffect(soundPath, 0.6); // Play at 60% volume
              console.log(`✅ Deploy effect sound triggered successfully: ${soundPath}`);
            } catch (error) {
              console.error(`❌ Error playing deploy effect sound:`, error);
            }
          }
        }, delay);
      });
    }

    // Update the previous effects set
    const currentEffects = new Set();
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.playedOrders) return;
      
      ['front', 'flank'].forEach(zone => {
        if (player.playedOrders[zone]) {
          player.playedOrders[zone].forEach((unit) => {
            if (!unit || !unit.instanceId) return;
            
            const enhancedBy = Array.isArray(unit.enhancedBy) 
              ? unit.enhancedBy 
              : (unit.enhancedBy ? [unit.enhancedBy] : []);
            
            enhancedBy.forEach((enhancement) => {
              if (enhancement && enhancement.instanceId) {
                // Include status in key for proper tracking of status effects
                const statusKey = enhancement.effectType === 'status' 
                  ? (enhancement.status || (enhancement.statuses && enhancement.statuses.join('_')) || '')
                  : '';
                const key = `${playerId}_${unit.instanceId}_${enhancement.instanceId}_${enhancement.effectType}_${statusKey}_${enhancement.turn || 'unknown'}`;
                currentEffects.add(key);
              }
            });
          });
        }
      });
    });
    
    previousEffectsRef.current = currentEffects;
  }, [gameState, userId]);
};

